/**
 * Checkout page header — restaurant name, back button, address with location change.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, MapPin } from 'lucide-react'
import { Button, ThemeSwitcher, Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui'
import { LocationPicker } from './LocationPicker'
import type { MenuLocation, RestaurantInfo } from '@/hooks/useMenuData'

interface CheckoutHeaderProps {
  restaurantName: string
  pickupAddress: string
  isMultiLocation: boolean
  menuLocations: MenuLocation[]
  restaurant: RestaurantInfo | null
  onBackToMenu: () => void
}

export function CheckoutHeader({
  restaurantName,
  pickupAddress,
  isMultiLocation,
  menuLocations,
  restaurant,
  onBackToMenu,
}: CheckoutHeaderProps) {
  const { t } = useTranslation()
  const [showLocationDialog, setShowLocationDialog] = useState(false)

  return (
    <>
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">{restaurantName}</h1>
          <div className="flex items-center gap-1">
            <ThemeSwitcher />
            <Button variant="ghost" onClick={onBackToMenu} className="text-sm text-muted-foreground">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('common:backToMenu')}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{pickupAddress ? t('common:pickupAtAddress', { address: pickupAddress }) : t('common:pickup')}</span>
          {isMultiLocation && (
            <button
              type="button"
              onClick={() => setShowLocationDialog(true)}
              className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 hover:border-primary/60 transition-colors cursor-pointer min-h-[44px]"
            >
              <MapPin className="h-3 w-3" />
              {t('common:change')}
            </button>
          )}
        </p>
      </header>

      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg p-4 sm:p-6" hideCloseButton>
          <DialogHeader>
            <DialogTitle>{t('common:choosePickupLocation')}</DialogTitle>
          </DialogHeader>
          <LocationPicker
            locations={menuLocations}
            restaurant={restaurant}
            hideTitle
            onSelect={() => setShowLocationDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
